// src/components/tables/ClientOpportunitiesTable.jsx
import React from 'react';
import OpportunityNameLink from '../common/OpportunityNameLink';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Button,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material';

/**
 * Format date to short mm/dd/yy format
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  });
};

/**
 * Format currency value
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value) => {
  if (!value) return '$0';
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

/**
 * Get stage color for opportunity stage
 * @param {string} stage - Opportunity stage
 * @returns {string} Color code
 */
const getStageColor = (stage) => {
  if (!stage) return '#757575';
  const stageLower = stage.toLowerCase();
  
  if (stageLower === 'discovery' || stageLower === 'qualification') {
    return '#64B5F6'; // Blue for early stages
  }
  if (stageLower === 'proposal' || stageLower === 'negotiation') {
    return '#FFCF44'; // Yellow for mid stages
  }
  if (stageLower === 'closed won') {
    return '#03DAC6'; // Green for won
  }
  if (stageLower === 'closed lost') {
    return '#CF6679'; // Red for lost
  }
  return '#BB86FC'; // Purple for other stages
};

/**
 * ClientOpportunitiesTable component displays client opportunities
 * Similar to ClientActiveProjectsTable but for opportunities
 */
const ClientOpportunitiesTable = ({ 
  opportunities = [], 
  clientId,
  limit = 999,
  showMore = false,
  onShowMoreClick,
  isLoading = false
}) => {
  const theme = useTheme();
  
  // Background color from theme or fallback
  const bgColor = theme.palette.background.paper || '#1A1A1A';
  
  // Filter to show only open opportunities (not closed)
  const openOpportunities = opportunities.filter(opp => {
    const stage = opp.stage?.toLowerCase();
    return stage !== 'closed won' && stage !== 'closed lost';
  });
  
  // Display opportunities up to the limit
  const displayOpportunities = openOpportunities.slice(0, limit);

  if (isLoading) {
    return (
      <Box sx={{ 
        px: 1.5, 
        pt: 1.5, 
        pb: 1.5,
        backgroundColor: bgColor,
        borderRadius: 1,
        mt: 2
      }}>
        <Typography 
          sx={{ 
            fontSize: '0.8rem', 
            fontWeight: 600, 
            mb: 0.5,
            textTransform: 'uppercase',
            color: theme.palette.text.primary
          }}
        >
          Open Opportunities
        </Typography>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
            Loading opportunities...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      px: 1.5, 
      pt: 1.5, 
      pb: showMore ? 1 : 1.5,
      backgroundColor: bgColor,
      borderRadius: 1,
      mt: 2
    }}>
      <Typography 
        sx={{ 
          fontSize: '0.8rem', 
          fontWeight: 600, 
          mb: 0.5,
          textTransform: 'uppercase',
          color: theme.palette.text.primary
        }}
      >
        Open Opportunities ({openOpportunities.length})
      </Typography>

      <Table 
        size="small" 
        sx={{ 
          mb: 0.5,
          '& .MuiTableCell-root': {
            py: 0.5,
            px: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
            fontSize: '0.7rem'
          },
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            color: theme.palette.text.secondary,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            pb: 0.25
          }
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Opportunity</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Close</TableCell>
            <TableCell>Stage</TableCell>
            <TableCell align="center">%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayOpportunities && displayOpportunities.length > 0 ? (
            displayOpportunities.map((opportunity, index) => (
              <TableRow 
                key={opportunity.opportunity_id || index}
                sx={{
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                    cursor: 'pointer'
                  }
                }}
              >
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '140px'
                    }}
                    title={opportunity.name || opportunity.opportunity_name || opportunity.title || opportunity.description || 'Unknown Opportunity'}
                  >
                    <OpportunityNameLink opportunity={opportunity}>
                      {opportunity.name || opportunity.opportunity_name || opportunity.title || opportunity.description || 'Unknown Opportunity'}
                    </OpportunityNameLink>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box component="span" sx={{ 
                    color: 'white',
                    fontWeight: 'medium'
                  }}>
                    {formatCurrency(opportunity.expected_value)}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'white' }}>
                  {formatShortDate(opportunity.expected_close_date)}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={opportunity.stage || 'Unknown'} 
                    size="small"
                    sx={{ 
                      fontSize: '0.6rem',
                      height: 18,
                      bgcolor: `${getStageColor(opportunity.stage)}30`,
                      color: getStageColor(opportunity.stage),
                      fontWeight: 'medium'
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ 
                  color: (opportunity.win_probability || 0) >= 0.7 ? '#03DAC6' : 
                         (opportunity.win_probability || 0) >= 0.4 ? '#FFCF44' : 
                         '#CF6679',
                  fontWeight: 'bold'
                }}>
                  {opportunity.win_probability_percentage || `${Math.round((opportunity.win_probability || 0) * 100)}%`}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell 
                colSpan={5} 
                align="center" 
                sx={{ 
                  py: 2,
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic'
                }}
              >
                No open opportunities
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* "Show more" button */}
      {showMore && onShowMoreClick && openOpportunities.length > displayOpportunities.length && (
        <Button 
          variant="text" 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            onShowMoreClick();
          }}
          sx={{ 
            fontSize: '0.65rem', 
            color: theme.palette.primary.main,
            display: 'block',
            margin: '0 auto',
            padding: '2px 8px',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.08)'
            }
          }}
        >
          SHOW MORE
        </Button>
      )}
    </Box>
  );
};

ClientOpportunitiesTable.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string,
    name: PropTypes.string,
    stage: PropTypes.string,
    expected_value: PropTypes.number,
    expected_close_date: PropTypes.string,
    win_probability: PropTypes.number
  })),
  clientId: PropTypes.string.isRequired,
  limit: PropTypes.number,
  showMore: PropTypes.bool,
  onShowMoreClick: PropTypes.func,
  isLoading: PropTypes.bool
};

export default ClientOpportunitiesTable;
